import axiosServices from '@utils/axios';
import { useEffect, useMemo, useState } from 'react';
import generateMutedColor from 'src/utils/colorUtils';

const CATEGORY_STORAGE_KEY = 'codebook_categories';

export function useGetCodeColorByCategory(studyId, options = {}) {
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const { enabled = true } = options;

  useEffect(() => {
    if (!studyId || !enabled) {
      setLoading(false);
      return;
    }

    const loadCategories = async () => {
      const cached = localStorage.getItem(CATEGORY_STORAGE_KEY);
      const parsedCache = cached ? JSON.parse(cached) : {};
      if (parsedCache[studyId] && Date.now() - parsedCache[studyId].timestamp < 3600000) {
        setCategories(parsedCache[studyId].data.categories);
        return;
      }

      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      const url = `${backendUrl}/qualitative-studies/${studyId}/codebook/`;

      try {
        const response = await axiosServices.get(url);
        const result = response.data;

        if (result?.categories) {
          const updatedCache = {
            ...parsedCache,
            [studyId]: {
              data: result,
              timestamp: Date.now(),
            },
          };
          localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(updatedCache));
          setCategories(result.categories);
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Failed to fetch categories:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [studyId, enabled]);

  const coloredCategories = useMemo(() => {
    if (!categories) return null;

    const categoryNames = Object.keys(categories);
    return categoryNames.reduce((acc, categoryName, index) => {
      const category = categories[categoryName];
      const color = generateMutedColor(index);
      acc[categoryName.toLowerCase()] = { ...category, color };
      return acc;
    }, {});
  }, [categories]);

  const getColorByCodeName = (codeName) => {
    if (!coloredCategories) return null;

    for (const categoryName in coloredCategories) {
      const category = coloredCategories[categoryName];
      if (category.codes_included?.some((code) => code.toLowerCase() === codeName.toLowerCase())) {
        return category.color;
      }
    }

    return null;
  };

  const getColorByCategory = (categoryName) => {
    if (!coloredCategories || !categoryName) return null;

    const category = coloredCategories[categoryName.toLowerCase()];
    return category ? category.color : null;
  };

  return {
    loading,
    categories,
    getColorByCodeName,
    getColorByCategory,
  };
}
